import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PackageCheck, SearchCheck, ClipboardCheck, ReceiptText } from "lucide-react";
import { TcrLayout } from "@/tcr/TcrLayout";
import { StatusChip } from "@/tcr/ui";
import { NODES, KEY_LINE, STANDARDS_LINE } from "@/tcr/data";

const CARDS = [
    [PackageCheck, "Publish", "An authorised supplier publishes a bounded change package."],
    [SearchCheck, "Assess", "Each organisation evaluates the change locally."],
    [ClipboardCheck, "Release", "Technical and clinical roles authorise their own part of deployment."],
    [ReceiptText, "Prove", "Receipts connect the change to installation, acceptance and service outcome."],
];

export default function TcrLanding() {
    return (
        <TcrLayout>
            <section className="fade-up" data-testid="landing-hero">
                <div className="flex flex-wrap items-center gap-3">
                    <p className="font-mono text-xs uppercase tracking-[0.18em] text-slate-400">
                        KATLAS healthcare-technology demonstrator
                    </p>
                    <StatusChip tone="amber" testId="landing-mode-badge">SIMULATED</StatusChip>
                    <StatusChip tone="slate">SYNTHETIC DATA</StatusChip>
                </div>
                <h1 className="mt-5 max-w-4xl text-4xl font-medium leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
                    Trusted Change Release
                </h1>
                <p className="mt-3 text-lg font-medium text-blue-950">
                    From supplier change to verified service release across
                    organisational boundaries.
                </p>
                <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-600">
                    Coordinate supplier, service-partner and healthcare-provider change
                    without centralising their systems, evidence or authority. Separately
                    operated role nodes receive the same authoritative change package,
                    evaluate it locally, resolve dependencies, govern installation and
                    release, and prove the resulting outcome through attributable
                    receipts.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                    <Link
                        to="/release?tab=package"
                        data-testid="landing-cta-run-journey"
                        className="group inline-flex items-center gap-2 rounded-sm bg-blue-950 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900"
                    >
                        Run the release journey
                        <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" />
                    </Link>
                    <Link
                        to="/architecture"
                        data-testid="landing-cta-architecture"
                        className="inline-flex items-center rounded-sm border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition-colors duration-150 hover:bg-slate-50"
                    >
                        View the role architecture
                    </Link>
                </div>

                <div className="mt-10 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
                    {CARDS.map(([Icon, title, body], i) => (
                        <motion.div
                            key={title}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.06 * i, duration: 0.3 }}
                            data-testid={`landing-card-${title.toLowerCase()}`}
                            className="bg-white p-6"
                        >
                            <Icon className="h-5 w-5 text-blue-950" strokeWidth={1.5} />
                            <h3 className="mt-4 text-lg font-medium text-slate-900">{title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            <section className="mt-14 rounded-sm border border-slate-200 bg-white p-8" data-testid="landing-nodes">
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                    Five separately operated role nodes
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    {NODES.map((n) => (
                        <div key={n.id} className="rounded-sm border-2 border-dashed border-slate-300 p-3">
                            <p className="text-sm font-medium text-slate-900">{n.name}</p>
                            <p className="mt-1 text-xs leading-relaxed text-slate-500">{n.desc}</p>
                        </div>
                    ))}
                </div>
                <p
                    className="mt-6 border-l-2 border-blue-950 pl-4 text-sm font-medium leading-relaxed text-slate-900"
                    data-testid="landing-key-line"
                >
                    {KEY_LINE}
                </p>
                <p className="mt-3 text-xs leading-relaxed text-slate-500">{STANDARDS_LINE}</p>
            </section>
        </TcrLayout>
    );
}
